import { Component, OnInit, AfterViewInit } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { debounceTime } from 'rxjs';
import moment from 'moment';
import { formatarData, formatarPlaca, formatarCpfCnpj } from 'src/app/core/utils/utilities';
import {
  IGetRelatorioLaudoServicoListParams,
  IGetRelatorioLaudoServicoListResponse,
  IGetRelatorioLaudoServicoItem
} from 'src/app/services/ReportService/report.model';
import { ReportService } from 'src/app/services/ReportService/report.service';
import { AppExportBase64 } from 'src/app/services/ExportBase64/exportBase64.service';
import { IGetServico } from 'src/app/services/ServicoService/servico.model';
import { ServicoService } from 'src/app/services/ServicoService/servico.service';
import { CaixaService } from 'src/app/services/CaixaService/caixa.service';
import { IGetCaixasMonthYearDataAberturaResponse } from 'src/app/services/CaixaService/caixa.model';

type SelectOption = {
  name: string;
  value: number | null;
};

@Component({
  selector: 'app-relatorio-laudo-servico',
  templateUrl: './relatorio-laudo-servico.component.html',
  styleUrls: ['./relatorio-laudo-servico.component.scss']
})
export class RelatorioLaudoServicoComponent implements OnInit, AfterViewInit {
  form = this.fb.group({
    TX_PLCA_CAVA: [null as string | null],
    DT_INIC: [null as string | null],
    DT_FIM: [null as string | null],
    VL_SRVC: [null as number | null],
    NM_TRND: [null as string | null],
    PK_LAUD_SRVC: [null as number | null],
    NU_TIPO_PGMT: [null as number | null],
    FK_SRVC: [null as number | null],
    FK_CAIX: [null as number | null]
  });

  servicoOptions: SelectOption[] = [{ name: 'Todos', value: null }];
  caixaOptions: SelectOption[] = [{ name: 'Todos', value: null }];

  isLoading: boolean = false;
  numeroPagina: number = 0;
  maxPages: number = 1;
  tableData: Array<any> = [];
  valorTotalFiltros: number = 0;

  pesquisaParams = {
    PAGE_NUMBER: 1,
    PAGE_SIZE: 20
  };

  readonly formasPagamento = [
    { value: null, name: 'Todos' },
    { value: 1, name: 'Cartão de Crédito' },
    { value: 19, name: 'Cartão de Débito' },
    { value: 9, name: 'Dinheiro' },
    { value: 27, name: 'PIX' },
    { value: 28, name: 'A Prazo' }
  ];

  tableColumns = [
    { header: 'Nº do Serviço', key: 'numeroServico', type: 'text' },
    { header: 'Data do Serviço', key: 'dataServico', type: 'text' },
    { header: 'Serviço', key: 'servico', type: 'text' },
    { header: 'Valor', key: 'valorServico', type: 'text' },
    { header: 'Forma de Pagamento', key: 'formaPagamento', type: 'text' },
    { header: 'Caixa', key: 'caixa', type: 'text' },
    { header: 'Placa', key: 'placa', type: 'text' },
    { header: 'CPF', key: 'cpf', type: 'text' },
    { header: 'Motorista', key: 'motorista', type: 'text' },
    { header: 'Empresa', key: 'empresa', type: 'text' }
  ];

  constructor(
    private fb: FormBuilder,
    private reportService: ReportService,
    private appExportBase64: AppExportBase64,
    private servicoService: ServicoService,
    private caixaService: CaixaService
  ) {
    const fim = moment().endOf('day');
    const inicio = moment().startOf('month').startOf('day');
    this.form.patchValue({
      DT_INIC: inicio.format('YYYY-MM-DDTHH:mm'),
      DT_FIM: fim.format('YYYY-MM-DDTHH:mm')
    });
  }

  ngOnInit(): void {
    this.loadServicos();
    this.loadCaixas();
    this.loadData();
  }

  ngAfterViewInit(): void {
    this.form.valueChanges.pipe(debounceTime(300)).subscribe(() => {
      this.pesquisaParams.PAGE_NUMBER = 1;
      this.numeroPagina = 0;
      this.loadData();
    });
  }

  mudarPagina(pagina: number): void {
    this.pesquisaParams.PAGE_NUMBER = pagina + 1;
    this.numeroPagina = pagina;
    this.loadData();
  }

  private createPayload(): IGetRelatorioLaudoServicoListParams {
    const fkSrvc = Number(this.form.controls.FK_SRVC.value);
    const fkCaix = Number(this.form.controls.FK_CAIX.value);

    return {
      TX_PLCA_CAVA: this.form.controls.TX_PLCA_CAVA.value ?? undefined,
      DT_INIC: this.form.controls.DT_INIC.value ?? undefined,
      DT_FIM: this.form.controls.DT_FIM.value ?? undefined,
      VL_SRVC: this.form.controls.VL_SRVC.value ?? undefined,
      NM_TRND: this.form.controls.NM_TRND.value ?? undefined,
      PK_LAUD_SRVC: this.form.controls.PK_LAUD_SRVC.value ?? undefined,
      NU_TIPO_PGMT: this.form.controls.NU_TIPO_PGMT.value ?? undefined,
      FK_SRVC: !Number.isNaN(fkSrvc) && fkSrvc > 0 ? fkSrvc : undefined,
      FK_CAIX: !Number.isNaN(fkCaix) && fkCaix > 0 ? fkCaix : undefined,
      PAGE_NUMBER: this.pesquisaParams.PAGE_NUMBER,
      PAGE_SIZE: this.pesquisaParams.PAGE_SIZE
    };
  }

  private loadServicos(): void {
    this.servicoService.GetServico().subscribe({
      next: response => {
        this.servicoOptions = [
          { name: 'Todos', value: null },
          ...response.map((servico: IGetServico) => ({
            name: servico.TX_TIPO_SRVC,
            value: servico.PK_SRVC
          }))
        ];
      }
    });
  }

  private loadCaixas(): void {
    this.caixaService.GetCaixas().subscribe({
      next: (caixas: IGetCaixasMonthYearDataAberturaResponse[]) => {
        const ordenados = [...(caixas ?? [])].sort((a, b) => {
          const dataA = new Date(a.DT_ABTR_CAIX).getTime();
          const dataB = new Date(b.DT_ABTR_CAIX).getTime();
          if (dataB !== dataA) {
            return dataB - dataA;
          }
          return b.PK_CAIX - a.PK_CAIX;
        });

        this.caixaOptions = [
          { name: 'Todos', value: null },
          ...ordenados.map(caixa => ({
            name: `#${caixa.PK_CAIX} - ${formatarData(caixa.DT_ABTR_CAIX) || '---'}`,
            value: caixa.PK_CAIX
          }))
        ];
      }
    });
  }

  loadData(): void {
    this.isLoading = true;
    const payload = this.createPayload();

    this.reportService.GetRelatorioLaudoServico(payload).subscribe({
      next: (response: IGetRelatorioLaudoServicoListResponse) => {
        this.tableData = response.ITEMS.map((item: IGetRelatorioLaudoServicoItem) => ({
          numeroServico: item.PK_SRVC_ENTR ?? item.PK_LAUD_SRVC ?? '---',
          dataServico: item.DT_SRVC_ENTR ? formatarData(item.DT_SRVC_ENTR) : '---',
          servico: item.TX_TIPO_SRVC?.trim() || '---',
          valorServico: item.VL_SRVC ? `R$ ${item.VL_SRVC.toFixed(2).replace('.', ',')}` : 'R$ 0,00',
          empresa: item.NM_TRND ?? '---',
          placa: formatarPlaca(item.TX_PLCA_CAVA) ?? '---',
          motorista: item.NM_MTRT ?? '---',
          cpf: item.TX_CPF ? formatarCpfCnpj(item.TX_CPF, 'cpf') : '---',
          formaPagamento: item.TX_TIPO_PGMT ?? '---',
          caixa: item.TX_CAIX?.trim() || '---'
        }));
        this.valorTotalFiltros = response.ITEMS[0]?.VL_TOTAL ?? 0;
        this.maxPages = response.TOTAL_PAGES;
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
      }
    });
  }

  exportarExcel(): void {
    this.isLoading = true;
    const payload = this.createPayload();

    this.reportService.GetReportLaudoServico({
      TX_PLCA_CAVA: payload.TX_PLCA_CAVA,
      DT_INIC: payload.DT_INIC,
      DT_FIM: payload.DT_FIM,
      VL_SRVC: payload.VL_SRVC,
      NM_TRND: payload.NM_TRND,
      PK_LAUD_SRVC: payload.PK_LAUD_SRVC,
      NU_TIPO_PGMT: payload.NU_TIPO_PGMT,
      FK_SRVC: payload.FK_SRVC,
      FK_CAIX: payload.FK_CAIX
    }).subscribe({
      next: (response) => {
        this.appExportBase64.downloadExcel(response.ReportBase64, "Relatório Financeiro.xlsx");
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
      }
    });
  }

  exportarPDF(): void {
    this.isLoading = true;
    const payload = this.createPayload();

    this.reportService.GetReportLaudoServicoPdf({
      TX_PLCA_CAVA: payload.TX_PLCA_CAVA,
      DT_INIC: payload.DT_INIC,
      DT_FIM: payload.DT_FIM,
      VL_SRVC: payload.VL_SRVC,
      NM_TRND: payload.NM_TRND,
      PK_LAUD_SRVC: payload.PK_LAUD_SRVC,
      NU_TIPO_PGMT: payload.NU_TIPO_PGMT,
      FK_SRVC: payload.FK_SRVC,
      FK_CAIX: payload.FK_CAIX
    }).subscribe({
      next: (response) => {
        this.appExportBase64.downloadPDF(response.ReportBase64, "Relatório Financeiro.pdf");
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
      }
    });
  }

  get valorTotalFormatado(): string {
    return `R$ ${this.valorTotalFiltros.toFixed(2).replace('.', ',')}`;
  }
}
